import { useState, useEffect } from 'react';
import { DocumentDetail, DocumentVersion, DocumentAnalytics } from '../types/documentVersion.types';
import { useDocumentAPI } from '../../../DocumentAPI';

interface UseDocumentVersionsProps {
  isOpen: boolean;
  documentId: string;
}

interface UseDocumentVersionsReturn {
  document: DocumentDetail | null;
  versions: DocumentVersion[];
  analytics: DocumentAnalytics | null;
  isLoading: boolean;
  error: string | null;
  reloadVersions: () => Promise<void>;
}

export const useDocumentVersions = ({ 
  isOpen, 
  documentId 
}: UseDocumentVersionsProps): UseDocumentVersionsReturn => {
  const documentAPI = useDocumentAPI();
  
  const [document, setDocument] = useState<DocumentDetail | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [analytics, setAnalytics] = useState<DocumentAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calculateAnalytics = (doc: DocumentDetail, versions: DocumentVersion[]): DocumentAnalytics => {
    const totalVersions = versions.length;
    const lastModified = doc.updated_at;
    const createdBy = doc.created_by_name || 'Unknown User';
    
    const totalSize = versions.reduce((sum, v) => sum + (v.size_bytes || 0), 0);
    const averageVersionSize = totalVersions > 0 ? Math.round(totalSize / totalVersions) : 0;
    
    // Calculate most active editor (simplified)
    const mostActiveEditor = doc.created_by_name || 'Unknown User';
    
    // Calculate version frequency
    const firstVersion = versions[versions.length - 1];
    const lastVersion = versions[0];
    const daysDiff = firstVersion && lastVersion ? 
      Math.ceil((new Date(lastVersion.created_at).getTime() - new Date(firstVersion.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
    const versionFrequency = daysDiff > 0 ? `${Math.round(totalVersions / daysDiff * 10) / 10} versions/day` : 'New document';
    
    // Calculate document age
    const documentAge = doc.created_at ? 
      `${Math.ceil((new Date().getTime() - new Date(doc.created_at).getTime()) / (1000 * 60 * 60 * 24))} days old` : 'Unknown';

    return {
      totalVersions,
      lastModified,
      createdBy,
      averageVersionSize,
      mostActiveEditor,
      versionFrequency,
      documentAge
    };
  };

  const loadDocumentData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load document details
      const doc = await documentAPI.getDocument(documentId);
      setDocument(doc);

      // Load versions and filter out v0 (initial empty versions)
      const versionsResponse = await documentAPI.getDocumentVersions(documentId);
      const filteredVersions = versionsResponse.versions.filter(version => version.version_number !== 'v0');
      const sortedVersions = filteredVersions.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setVersions(sortedVersions);

      // Calculate analytics (using filtered versions, excluding v0)
      const analytics = calculateAnalytics(doc, sortedVersions);
      setAnalytics(analytics);

    } catch (err) {
      console.error('Error loading document data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document data');
    } finally {
      setIsLoading(false);
    }
  };

  const reloadVersions = async () => {
    if (!document) return;
    
    try {
      const doc = await documentAPI.getDocument(documentId);
      setDocument(doc);

      const versionsResponse = await documentAPI.getDocumentVersions(documentId);
      const filteredVersions = versionsResponse.versions.filter(version => version.version_number !== 'v0');
      const sortedVersions = filteredVersions.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setVersions(sortedVersions);

      // Recalculate analytics
      const analytics = calculateAnalytics(doc, sortedVersions);
      setAnalytics(analytics);
    } catch (err) {
      console.error('Error reloading versions:', err);
      setError(err instanceof Error ? err.message : 'Failed to reload versions');
    }
  };

  // Load document and versions when modal opens
  useEffect(() => {
    if (!isOpen || !documentId) return;
    loadDocumentData();
  }, [isOpen, documentId]);

  return {
    document,
    versions,
    analytics,
    isLoading,
    error,
    reloadVersions
  };
};
